// utils/schedule.js
// Pure helpers for describing Cloud Scheduler cron windows in the admin UI.
//
// The keyword refresh runs on the Cloud Functions schedule '0 6 1 * *' in
// America/Los_Angeles (see functions/scheduledKeywords.js). Reproducing that
// here keeps the "next run" label honest without another round-trip, but it
// means this file and the cron expression must stay in sync.

/** Cron window of the monthly keyword refresh, mirrored from scheduledKeywords.js. */
export const KEYWORD_REFRESH_SCHEDULE = {
  cron: "0 6 1 * *",
  timeZone: "America/Los_Angeles",
  label: "1st of the month, 6:00am PT",
};

/** Cron window of the weekly content refresh, mirrored from scheduledBlogRefresh.js. */
export const CONTENT_REFRESH_SCHEDULE = {
  cron: "0 7 * * 3",
  timeZone: "America/Los_Angeles",
  label: "Wednesdays, 7:00am PT",
};

const PT_ZONE = "America/Los_Angeles";

/**
 * The UTC offset (in minutes) that America/Los_Angeles is behind UTC at `date`.
 * Derived from the formatted wall-clock time rather than hardcoded, so the
 * PST/PDT transition is handled without a date library.
 */
function ptOffsetMinutes(date) {
  // 'en-CA' yields an ISO-like YYYY-MM-DD, which parses back reliably.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_ZONE,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});

  // Hour 24 appears at midnight in some engines; normalise to 00.
  const hour = parts.hour === "24" ? "00" : parts.hour;
  const asUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(hour), Number(parts.minute), Number(parts.second)
  );
  return (date.getTime() - asUTC) / 60000;
}

/**
 * Build the instant corresponding to a Pacific wall-clock time.
 * Applies the offset twice: the first pass picks the offset from an
 * approximate instant, the second corrects it if that guess landed on the
 * other side of a DST boundary.
 */
function fromPacificWallClock(year, monthIndex, day, hour) {
  const naive = Date.UTC(year, monthIndex, day, hour, 0, 0);
  let guess = new Date(naive + ptOffsetMinutes(new Date(naive)) * 60000);
  guess = new Date(naive + ptOffsetMinutes(guess) * 60000);
  return guess;
}

/**
 * Next occurrence of the monthly keyword refresh (1st of month, 6am PT).
 *
 * @param {Date} [now] - reference instant, injectable for tests
 * @returns {Date}
 */
export function nextKeywordRefreshDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_ZONE, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});

  const year  = Number(parts.year);
  const month = Number(parts.month) - 1;

  // This month's run may still be ahead of us (only on the 1st, before 6am PT).
  const thisMonth = fromPacificWallClock(year, month, 1, 6);
  if (thisMonth.getTime() > now.getTime()) return thisMonth;

  return fromPacificWallClock(year, month + 1, 1, 6);
}

/**
 * Next occurrence of the weekly content refresh (Wednesdays, 7am PT).
 *
 * Day-of-week rather than day-of-month arithmetic: find the coming Wednesday in
 * Pacific terms, and only keep today if it IS Wednesday and 7am has not passed.
 *
 * @param {Date} [now] - reference instant, injectable for tests
 * @returns {Date}
 */
export function nextContentRefreshDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_ZONE, year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
  }).formatToParts(now).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});

  const year  = Number(parts.year);
  const month = Number(parts.month) - 1;
  const day   = Number(parts.day);

  // Intl weekday → 0-6 (Sun-Sat), avoiding getDay() which reports local time.
  const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const todayDow = WEEKDAYS[parts.weekday];
  const WEDNESDAY = 3;

  let daysAhead = (WEDNESDAY - todayDow + 7) % 7;

  // It's Wednesday: today's run only counts if 7am PT is still ahead.
  if (daysAhead === 0) {
    const todayRun = fromPacificWallClock(year, month, day, 7);
    if (todayRun.getTime() > now.getTime()) return todayRun;
    daysAhead = 7;
  }

  return fromPacificWallClock(year, month, day + daysAhead, 7);
}

/** Absolute date + time rendered in Pacific, e.g. "Sep 1, 2026, 6:00 AM PDT". */
export function formatPacific(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: PT_ZONE,
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(date);
}

/** Coarse relative label, e.g. "3 days ago" / "in 2 weeks". */
export function formatRelative(date, now = new Date()) {
  if (!date) return "";
  const diffMs   = date.getTime() - now.getTime();
  const absMs    = Math.abs(diffMs);
  const minute   = 60000;
  const hour     = 60 * minute;
  const day      = 24 * hour;

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (absMs < hour)  return rtf.format(Math.round(diffMs / minute), "minute");
  if (absMs < day)   return rtf.format(Math.round(diffMs / hour), "hour");
  if (absMs < 30 * day) return rtf.format(Math.round(diffMs / day), "day");
  return rtf.format(Math.round(diffMs / (30 * day)), "month");
}
